<template>
    <main
        :style="{ 'background-color': back }"
        class="no-scrollbar flex-1 relative overflow-y-auto focus:outline-none ct-bg-2 h-full"
    >
        <div
            ref="containerRef"
            class="relative flex flex-1 min-h-full h-full overflow-hidden justify-center"
        >
            <CardPreloader v-if="!iframeLoaded" />

            <div
                ref="viewportRef"
                class="relative h-full ct-border-2 border-r border-l w-full"
                :class="{ hidden: !iframeLoaded }"
                :style="viewportStyle"
            >
                <iframe
                    @load="setIframeLoaded()"
                    class="transition-opacity h-full"
                    :class="[
                        iframeLoaded ? 'relative' : 'opacity-0 absolute',
                        previewStore.viewport !== 'desktop'
                            ? 'border-l border-r ct-border-2 border-dashed'
                            : '',
                    ]"
                    id="iframe"
                    :width="width ? width : previewStore.screenWidth"
                    :height="height ? height : '100%'"
                    :src="srcWithLang"
                    frameborder="0"
                ></iframe>
            </div>
        </div>
    </main>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { CardPreloader } from "@easy/ui/cards";
import { usePagePreviewStore } from "@/apps/page/store/page-preview.store";

interface Props {
    width?: string;
    height?: string;
    src: string;
    back?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: "action", value: any): void;
}>();

const store = useStore();
const route = useRoute();
const router = useRouter();
const previewStore = usePagePreviewStore();

const iframeLoaded = computed(() => store.getters.iframeLoaded);
const previewLang = computed(() => store.state.previewLang);
const containerRef = ref<HTMLElement | null>(null);
const viewportRef = ref<HTMLElement | null>(null);

const calculateInitialScale = () => {
    if (!containerRef.value) return;

    const containerWidth = containerRef.value.getBoundingClientRect().width;
    let contentWidth = 1024;

    if (previewStore.viewport === "mobile") {
        contentWidth = 380;
    }

    const rawScale = Math.min(1, containerWidth / contentWidth);
    const newScale = Math.floor(rawScale * 20) / 20; // Rounds to nearest floor 0.05
    // Ensure scale stays within bounds
    previewStore.setScale(Math.max(0.2, Math.min(1, newScale)));
};

const updateWidth = () => {
    if (!containerRef.value) return;
    const rect = containerRef.value.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const remainingWidth = windowWidth - rect.x;
    containerRef.value.style.width = `${remainingWidth}px`;
};

const getMarginLeft = (scale: number) => {
    if (!containerRef.value || !viewportRef.value) return "initial";
    const containerWidth = containerRef.value.getBoundingClientRect().width;
    const scaledWidth = 1024 * scale;

    if (scaledWidth > containerWidth) {
        containerRef.value.style.overflowX = "auto";
        return `${scaledWidth - containerWidth}px`;
    }
    containerRef.value.style.overflowX = "hidden";
    return "initial";
};

const viewportStyle = computed(() => {
    const baseStyle = {
        transform: `scale(${previewStore.scale})`,
        transformOrigin: "center top",
        height: `${100 / previewStore.scale}%`,
    };

    if (previewStore.viewport === "desktop") {
        return {
            ...baseStyle,
            minWidth: "1030px", // 1024px + 6px some buffer (rounding erros, etc.)
            marginLeft: getMarginLeft(previewStore.scale),
        };
    }
    if (previewStore.viewport === "tablet") {
        return { ...baseStyle, maxWidth: "1024px" };
    }
    if (previewStore.viewport === "mobile") {
        return { ...baseStyle, maxWidth: "380px" };
    }
    return baseStyle;
});

const srcWithLang = computed(() => `${props.src}?lang=${previewLang.value}`);

const setIframeLoaded = () => store.dispatch("setIframeLoaded");

const routeTo = (forcedView: string) => {
    const { name, query } = route;
    router.push({ name, query: { ...query, view: forcedView } });
};

const showAlert = (alert: any) => store.commit("showAlert", alert);

onMounted(() => {
    updateWidth();
    calculateInitialScale();

    window.addEventListener("resize", updateWidth);

    watch(
        () => [previewStore.scale, iframeLoaded.value],
        ([newScale, newIframeLoaded]) => {
            const iframe = document.getElementById("iframe") as HTMLIFrameElement | null;
            if (iframe?.contentWindow && newIframeLoaded) {
                iframe.contentWindow.postMessage({ type: "scaleUpdate", scale: newScale }, "*");
            }
        },
        { immediate: true }
    );

    window.onmessage = (e) => {
        const data = e.data;
        if (data.setView) {
            routeTo(data.setView);
        }
        if (data.setRoute) {
            const { name, query } = data.setRoute;
            router.push({ name, query });
        }
        if (data.alert) {
            showAlert(data.alert);
        }
        if (data.action) {
            emit("action", data.action);
        }
    };
});

onUnmounted(() => {
    window.removeEventListener("resize", updateWidth);
});
</script>
